import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import LuxPlusLandingPageContent from 'luxPlus/components/LuxPlusLandingPageContent'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import LuxPlusMonthLandingPageContent from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLandingPageContent'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { selectLoggedIn } from 'selectors/accountSelectors'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'
import useQueryParams from 'hooks/useQueryParams'
import { LUX_PLUS_PROMOTIONAL_OFFER_PARAM, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { replaceWithRegion } from 'actions/NavigationActions'
import getLuxPlusShouldRedirectToAccountPage from 'luxPlus/selectors/getLuxPlusShouldRedirectToAccountPage'

function LuxPlusLandingPage() {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchProductSubscription())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shouldRedirectToAccountPage = useAppSelector(getLuxPlusShouldRedirectToAccountPage)
  useEffect(() => {
    if (!shouldRedirectToAccountPage) return
    dispatch(replaceWithRegion(`/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`))
  }, [dispatch, shouldRedirectToAccountPage])

  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const currentTakeover = useCurrentSiteTakeoverV2()

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const openLoginModal = useCallback(() => showModal<AccountAccessModalResult>(<AccountAccessModal />), [showModal])

  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const queryParams = useQueryParams()
  const hasPromotionParam = queryParams.get(LUX_PLUS_PROMOTIONAL_OFFER_PARAM) === 'true'

  const isLuxPlusPromotionalOfferEnabled = useMemo(() => {
    return (
      regionCode === 'AU' &&
      !isLuxPlusMember &&
      hasPromotionParam
    )
  }, [regionCode, isLuxPlusMember, hasPromotionParam])

  const purchasePromotionalLuxPlusOffer = useCallback(async() => {
    if (!isLuxPlusPromotionalOfferEnabled) return
    if (!isLoggedIn) {
      const loginResult = await openLoginModal()
      if (loginResult.loggedIn) {
        dispatch(purchaseLuxPlus({ hasJoinFee: false }))
      }
    } else {
      dispatch(purchaseLuxPlus({ hasJoinFee: false }))
    }
  }, [isLuxPlusPromotionalOfferEnabled, isLoggedIn, openLoginModal, dispatch])

  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)

  useEffect(() => {
    purchasePromotionalLuxPlusOffer()
  }, [purchasePromotionalLuxPlusOffer, subscriptionProduct.offers])

  if (!isSpoofed && currentTakeover?.id === LUX_PLUS_MONTH_TAKEOVER_ID) return <LuxPlusMonthLandingPageContent />

  return <LuxPlusLandingPageContent />
}

export default LuxPlusLandingPage
